/**
 * Device-token vault (T07-22): the 90-day device token is stored **encrypted** in IndexedDB
 * (`auth` table) with an AES-GCM key created by WebCrypto as **non-extractable** — the key object
 * lives in the same table (IndexedDB can hold a `CryptoKey`), but its bytes can never be read by
 * script, so a copied database file / XSS dump of storage alone does not reveal the token.
 * Nothing ever goes to `localStorage` / `sessionStorage`. (Not a secure enclave — design/08 §6:
 * Cloudflare Access + revoke from the admin are the real protection.)
 */
import type { PosDatabase } from "./db";

const KEY_ID = "vault-key";
const DEVICE_ID = "device";

export interface StoredDevice {
  registerId: string;
  deviceId: string;
  deviceName: string;
  /** ISO — when the device token stops working (pairing again is needed). */
  expiresAt: string | null;
  /** Register code shown on screen (R01). */
  registerCode?: string | null;
}

interface EncryptedDevice extends StoredDevice {
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
}

async function vaultKey(db: PosDatabase): Promise<CryptoKey> {
  const row = await db.auth.get(KEY_ID);
  if (row && row.value instanceof CryptoKey) {
    return row.value;
  }
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
    "encrypt",
    "decrypt",
  ]);
  await db.auth.put({ key: KEY_ID, value: key });
  return key;
}

export async function saveDeviceToken(
  db: PosDatabase,
  device: StoredDevice,
  deviceToken: string,
): Promise<void> {
  const key = await vaultKey(db);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(deviceToken),
  );
  const record: EncryptedDevice = { ...device, iv, ciphertext };
  await db.auth.put({ key: DEVICE_ID, value: record });
}

export async function loadDevice(
  db: PosDatabase,
): Promise<{ device: StoredDevice; deviceToken: string } | null> {
  const row = await db.auth.get(DEVICE_ID);
  const value = row?.value as EncryptedDevice | undefined;
  if (!value?.ciphertext || !value.iv) {
    return null;
  }
  try {
    const key = await vaultKey(db);
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: value.iv as Uint8Array<ArrayBuffer> },
      key,
      value.ciphertext,
    );
    const { iv: _iv, ciphertext: _c, ...device } = value;
    return { device, deviceToken: new TextDecoder().decode(plain) };
  } catch {
    // key gone / data tampered: the tablet must be paired again
    await db.auth.delete(DEVICE_ID);
    return null;
  }
}

/** Forget the pairing (revoked by the admin, expired, or "unpair" in settings). */
export async function clearDevice(db: PosDatabase): Promise<void> {
  await db.auth.delete(DEVICE_ID);
}
